import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { dateTimeFormatter } from 'src/libs/dateTimeFormatter';

interface ReviewEntry {
  created_at?: string;
  score?: number;
  hallucination_detected?: boolean;
  review_title?: string;
  review_bullets?: string[];
  recommendation?: string;
  icon?: string;
  generating?: boolean;
  error?: string | null;
  goals_achieved?: number;
  total_goals?: number;
}

interface DataPoint {
  date: number;
  score: number;
  title: string;
  bullets: string[];
  recommendation?: string;
  hallucination: boolean;
  icon?: string;
  createdAt: string;
  idx: number; // sequential index for even spacing
  goalsAchieved?: number;
  totalGoals?: number;
  goalsIncreased: boolean;
}

interface TimelineOptions {
  xSpacing: 'even' | 'time';
  height: number;
  minWidthPerPoint: number;
  minChartWidth: number;
  scroll: boolean; // allow horizontal scroll when needed
  contain: boolean; // fill parent width without scroll (uses ResponsiveContainer)
  showGrid: boolean;
  gridStrokeDasharray: string;
  gridStrokeOpacity: number;
  showAxisLabels: boolean;
  showAxisLine: boolean;
  showTickLines: boolean;
  fadeTicksOnHover: boolean;
  tickOpacityHover: number; // 0..1
  tickSize: number; // tick mark size in px
  lineType: 'monotone' | 'linear' | 'natural' | 'step' | 'stepAfter' | 'stepBefore';
  strokeWidth: number;
  dotRadius: number;
  containerPadding: number; // MUI spacing units
  margin: { top: number; right: number; bottom: number; left: number };
  tooltipMaxWidth: number;
  enableAnimation: boolean;
  // score bubble options
  showScoreLeftColumn: boolean;
  showScoreTopRight: boolean;
  scoreBubbleLeftSize: number; // px
  scoreBubbleTopRightSize: number; // px
  scoreBubbleVariant: 'circle' | 'pill';
  scoreBubbleBgAlpha: number; // 0..1 background opacity
  scoreBubbleFontWeight: number;
  // tooltip container visuals
  tooltipBgAlpha: number; // 0..1
  tooltipBorderAlpha: number; // 0..1
  tooltipBackdropBlur: number; // px
  // axis paddings
  xAxisPaddingLeft: number;
  xAxisPaddingRight: number;
  // explicit axis reserved space
  xAxisHeight: number;
  yAxisWidth: number;
  // auto-scroll behavior
  autoScrollToLatest: boolean;
  autoScrollBehavior: 'auto' | 'smooth';
  // goal pulse visuals
  goalPulseEnabled: boolean;
  goalPulseDurationMs: number;
  goalPulseRadiusExtra: number;
  goalPulseOpacityFrom: number; // 0..1
  // collapsed/unfocused state
  startCollapsed: boolean;
  collapsedHeight: number;
  collapsedOpacity: number;
  expandOnClick: boolean;
  autoCollapseOnMouseLeave?: boolean;
  // line rendering options
  curveLines?: boolean; // draw a single curved line through all points
  useGradientStroke?: boolean; // color the line with a gradient derived from point colors
  // expand/collapse animation
  expandCollapseDurationMs?: number;
  expandCollapseEasing?: string;
}

const defaultOptions: TimelineOptions = {
  xSpacing: 'even',
  height: 160,
  minWidthPerPoint: 120,
  minChartWidth: 600,
  scroll: true,
  contain: false,
  showGrid: false,
  gridStrokeDasharray: '3 3',
  gridStrokeOpacity: 0.1,
  showAxisLabels: false,
  showAxisLine: false,
  showTickLines: true,
  fadeTicksOnHover: true,
  tickOpacityHover: 0.25,
  tickSize: 4,
  lineType: 'monotone',
  strokeWidth: 2,
  dotRadius: 4,
  containerPadding: 0,
  margin: { top: 0, right: 0, left: 0, bottom: 0 },
  tooltipMaxWidth: 320,
  enableAnimation: false,
  showScoreLeftColumn: true,
  showScoreTopRight: false,
  scoreBubbleLeftSize: 28,
  scoreBubbleTopRightSize: 24,
  scoreBubbleVariant: 'circle',
  scoreBubbleBgAlpha: 0.15,
  scoreBubbleFontWeight: 700,
  tooltipBgAlpha: 0.9,
  tooltipBorderAlpha: 0,
  tooltipBackdropBlur: 6,
  xAxisPaddingLeft: 0,
  xAxisPaddingRight: 0,
  xAxisHeight: 0,
  yAxisWidth: 0,
  autoScrollToLatest: true,
  autoScrollBehavior: 'auto',
  goalPulseEnabled: true,
  goalPulseDurationMs: 1600,
  goalPulseRadiusExtra: 6,
  goalPulseOpacityFrom: 0.28,
  startCollapsed: true,
  collapsedHeight: 50,
  collapsedOpacity: 0.5,
  expandOnClick: true,
  autoCollapseOnMouseLeave: true,
  curveLines: true,
  useGradientStroke: true,
  expandCollapseDurationMs: 260,
  expandCollapseEasing: 'cubic-bezier(0.4, 0.0, 0.2, 1)',
};

/**
 * Timeline component props
 * - `options` lets parent override visuals/behavior
 * - Optional hooks emit hover/click/visibility events to parent
 */
interface Props {
  reviews?: ReviewEntry[];
  options?: Partial<TimelineOptions>;
  onPointHover?: (point: DataPoint | null, index: number | null) => void;
  onPointClick?: (point: DataPoint, index: number) => void;
  onHoverStateChange?: (hovering: boolean) => void;
  onTooltipVisibilityChange?: (visible: boolean, point?: DataPoint) => void;
  expanded?: boolean; // controlled expanded state
  defaultExpanded?: boolean; // uncontrolled initial state
  onExpandedChange?: (expanded: boolean) => void;
}

const ConversationQualityTimeline: React.FC<Props> = ({ reviews = [], options, onPointHover, onPointClick, onHoverStateChange, onTooltipVisibilityChange, expanded, defaultExpanded, onExpandedChange }) => {
  const theme = useTheme();
  const opts: TimelineOptions = React.useMemo(() => ({ ...defaultOptions, ...(options || {}) }), [options]);
  const [isHovering, setIsHovering] = React.useState(false);
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const didAutoScrollRef = React.useRef(false);
  const [internalExpanded, setInternalExpanded] = React.useState<boolean>(() => {
    if (typeof defaultExpanded === 'boolean') return defaultExpanded;
    return !opts.startCollapsed;
  });
  const isExpanded = typeof expanded === 'boolean' ? expanded : internalExpanded;

  const data: DataPoint[] = React.useMemo(() => {
    const base = (reviews || [])
      .filter(r => r && !r.generating && !r.error && r.created_at && typeof r.score === 'number')
      .sort((a, b) => new Date(a.created_at as string).getTime() - new Date(b.created_at as string).getTime())
      .map((r, i) => ({
        date: new Date(r.created_at as string).getTime(),
        score: r.score as number,
        title: r.review_title || '',
        bullets: r.review_bullets || [],
        recommendation: r.recommendation,
        hallucination: r.hallucination_detected ?? false,
        icon: r.icon,
        createdAt: r.created_at as string,
        idx: i,
        goalsAchieved: typeof r.goals_achieved === 'number' ? r.goals_achieved : undefined,
        totalGoals: typeof r.total_goals === 'number' ? r.total_goals : undefined,
        goalsIncreased: false,
      }));
    return base.map((dp, i) => {
      const prev = base[i - 1];
      const prevGoals = prev?.goalsAchieved ?? 0;
      const currGoals = dp.goalsAchieved ?? 0;
      return { ...dp, goalsIncreased: i > 0 && currGoals > prevGoals };
    });
  }, [reviews]);

  const latest = React.useMemo(() => {
    return reviews.length
      ? [...reviews].sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime())[reviews.length - 1]
      : undefined;
  }, [reviews]);

  const getColor = React.useCallback((score: number) => {
    if (score >= 80) return theme.palette.success.main;
    if (score >= 40) return theme.palette.warning.main;
    return theme.palette.error.main;
  }, [theme]);

  const segments = React.useMemo(() => {
    const segs: { data: DataPoint[]; color: string }[] = [];
    for (let i = 0; i < data.length - 1; i++) {
      const color = getColor((data[i].score + data[i + 1].score) / 2);
      segs.push({ data: [data[i], data[i + 1]], color });
    }
    return segs;
  }, [data, getColor]);

  const renderDot = (props: any) => {
    const { cx, cy, payload } = props;
    const color = getColor(payload.score);
    return (
      <g>
        {opts.goalPulseEnabled && payload.goalsIncreased && (
          <g>
            <circle cx={cx} cy={cy} r={opts.dotRadius + 1} fill={color} opacity={opts.goalPulseOpacityFrom}>
              <animate attributeName="r" from={opts.dotRadius + 1} to={opts.dotRadius + opts.goalPulseRadiusExtra} dur={`${opts.goalPulseDurationMs}ms`} repeatCount="indefinite" />
              <animate attributeName="opacity" from={opts.goalPulseOpacityFrom} to="0" dur={`${opts.goalPulseDurationMs}ms`} repeatCount="indefinite" />
            </circle>
          </g>
        )}
        <circle cx={cx} cy={cy} r={opts.dotRadius} fill={color} stroke={theme.palette.background.paper} strokeWidth={1} />
        {payload.icon && (
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize={12}>{payload.icon}</text>
        )}
        {payload.hallucination && (
          <text x={cx} y={cy - (payload.icon ? 20 : 8)} textAnchor="middle" fontSize={12}>⚠️</text>
        )}
      </g>
    );
  };

  const renderScoreBubble = (value: number, color: string, size: number, variant: 'circle' | 'pill' = opts.scoreBubbleVariant) => {
    const rounded = Math.round(value);
    const digits = String(rounded).length;
    const isPill = variant === 'pill' || digits >= 3;
    const paddingX = isPill ? Math.max(8, Math.round(size * 0.35)) : 0;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: size,
          minWidth: size,
          px: isPill ? `${paddingX}px` : 0,
          borderRadius: 999,
          bgcolor: alpha(color, opts.scoreBubbleBgAlpha),
          color,
          border: `1px solid ${alpha(color, 0.35)}`,
          fontWeight: opts.scoreBubbleFontWeight,
          fontSize: size <= 22 ? 12 : size <= 28 ? 13 : 14,
          lineHeight: 1,
        }}
      >
        {rounded}
      </Box>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const p = payload[0].payload;
      // notify parent about visibility
      onTooltipVisibilityChange && onTooltipVisibilityChange(true, p);
      return (
        <Box sx={{ position: 'relative', bgcolor: alpha(theme.palette.background.paper, opts.tooltipBgAlpha), border: 1, borderColor: alpha(theme.palette.divider, opts.tooltipBorderAlpha), p: 1.25, borderRadius: 1.5, maxWidth: opts.tooltipMaxWidth, backdropFilter: `blur(${opts.tooltipBackdropBlur}px)` }}>
          {/* Top-right score bubble */}
          {(!opts.showScoreLeftColumn && opts.showScoreTopRight) && (
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              {renderScoreBubble(p.score, getColor(p.score), opts.scoreBubbleTopRightSize)}
            </Box>
          )}

          {/* Header with left score bubble and right details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: (!opts.showScoreLeftColumn && opts.showScoreTopRight) ? 5 : 0 }}>
            {opts.showScoreLeftColumn && (
              <Box sx={{ width: opts.scoreBubbleLeftSize, minWidth: opts.scoreBubbleLeftSize, display: 'flex', justifyContent: 'center' }}>
                {renderScoreBubble(p.score, getColor(p.score), opts.scoreBubbleLeftSize)}
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">{dateTimeFormatter(p.createdAt, 'date_time')}</Typography>
              {p.title && (
                <Typography variant="subtitle2" sx={{ mt: 0.25 }} noWrap>
                  {p.icon && `${p.icon} `}
                  {p.title}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Details list */}
          <Box component="ul" sx={{ pl: 2, m: 0, mt: 0.5 }}>
            {p.bullets.map((b: string, idx: number) => (
              <Typography component="li" variant="body2" key={idx}>{b}</Typography>
            ))}
            {(typeof p.goalsAchieved === 'number' || typeof p.totalGoals === 'number') && (
              <Typography component="li" variant="body2">
                Goals: {p.goalsAchieved ?? 0}/{p.totalGoals ?? 0}
              </Typography>
            )}
            {p.recommendation && (
              <Typography component="li" variant="body2"><strong>Recommendation:</strong> {p.recommendation}</Typography>
            )}
          </Box>
          {p.hallucination && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.5 }}>⚠️ Hallucination detected</Typography>
          )}
        </Box>
      );
    }
    // hidden
    onTooltipVisibilityChange && onTooltipVisibilityChange(false);
    return null;
  };

  // Precompute layout and attach effects BEFORE any early returns to satisfy hooks rules
  const chartWidth = Math.max(data.length * opts.minWidthPerPoint, opts.minChartWidth);
  const xIsEven = opts.xSpacing === 'even';
  const xAxisType = xIsEven ? 'category' : 'number';
  const xDataKey = xIsEven ? 'idx' : 'date';
  const gradientId = React.useMemo(() => `cqtGradient-${Math.random().toString(36).slice(2, 9)}`, []);
  const gradientStops = React.useMemo(() => {
    const n = data.length;
    if (n === 0) return [] as Array<{ offset: number; color: string }>;
    if (n === 1) return [{ offset: 0, color: getColor(data[0].score) }, { offset: 100, color: getColor(data[0].score) }];
    return data.map((d, i) => ({ offset: (i / (n - 1)) * 100, color: getColor(d.score) }));
  }, [data, getColor]);

  // Auto-scroll to show the latest point at the right edge on initial mount
  React.useEffect(() => {
    if (!opts.scroll || opts.contain || !opts.autoScrollToLatest) return;
    const el = wrapperRef.current;
    if (!el || didAutoScrollRef.current) return;
    const id = requestAnimationFrame(() => {
      try {
        (el as any).scrollTo({ left: el.scrollWidth, behavior: opts.autoScrollBehavior as any });
      } catch (_e) {
        el.scrollLeft = el.scrollWidth;
      }
      didAutoScrollRef.current = true;
    });
    return () => cancelAnimationFrame(id);
  }, [opts.scroll, opts.contain, opts.autoScrollToLatest, opts.autoScrollBehavior, chartWidth]);

  // Hide entirely when there are 0 or 1 reviews (only a single data point)
  if (data.length <= 1) return null;



  const isCollapsed = !isExpanded;
  const effectiveHeight = isCollapsed ? opts.collapsedHeight : opts.height;

  return (
    <Box
      ref={wrapperRef}
      sx={{ width: '100%', overflowX: (!isCollapsed && opts.scroll) ? 'auto' : 'hidden', p: opts.containerPadding, opacity: isCollapsed ? opts.collapsedOpacity : 1, transition: `opacity 200ms ease`, cursor: isCollapsed && opts.expandOnClick ? 'pointer' : 'default' }}
      onMouseEnter={() => { if (!isCollapsed) { setIsHovering(true); onHoverStateChange && onHoverStateChange(true); } }}
      onMouseLeave={() => {
        if (!isCollapsed) {
          setIsHovering(false);
          setHoveredIndex(null);
          onHoverStateChange && onHoverStateChange(false);
          onPointHover && onPointHover(null, null);
          if (opts.autoCollapseOnMouseLeave) {
            setInternalExpanded(false);
            onExpandedChange && onExpandedChange(false);
          }
        }
      }}
      onClick={() => { if (isCollapsed && opts.expandOnClick) { setInternalExpanded(true); onExpandedChange && onExpandedChange(true); } }}
    >
      {opts.contain ? (
        <Box sx={{ height: effectiveHeight, transition: `height ${opts.expandCollapseDurationMs}ms ${opts.expandCollapseEasing}` }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={opts.margin}
              onMouseMove={isCollapsed ? undefined : ((state: any) => {
                const idx = state && typeof state.activeTooltipIndex === 'number' ? state.activeTooltipIndex : null;
                if (idx !== null && idx !== hoveredIndex) {
                  setHoveredIndex(idx);
                  onPointHover && onPointHover(data[idx], idx);
                }
              })}
              onMouseLeave={isCollapsed ? undefined : (() => {
                setHoveredIndex(null);
                onPointHover && onPointHover(null, null);
              })}
              onClick={isCollapsed ? undefined : ((state: any) => {
                const idx = state && typeof state.activeTooltipIndex === 'number' ? state.activeTooltipIndex : null;
                if (idx !== null) {
                  onPointClick && onPointClick(data[idx], idx);
                }
              })}
            >
              {opts.showGrid && (
                <CartesianGrid strokeDasharray={opts.gridStrokeDasharray} stroke={theme.palette.divider} strokeOpacity={opts.gridStrokeOpacity} />
              )}
              {opts.useGradientStroke && (
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                    {gradientStops.map((s, idx) => (
                      <stop key={idx} offset={`${s.offset}%`} stopColor={s.color} />
                    ))}
                  </linearGradient>
                </defs>
              )}
              <XAxis
                type={xAxisType as any}
                dataKey={xDataKey}
                {...(!xIsEven
                  ? { domain: ['dataMin', 'dataMax'] as any, tickFormatter: (ts: any) => dateTimeFormatter(ts, 'date_only'), padding: { left: opts.xAxisPaddingLeft, right: opts.xAxisPaddingRight } as any }
                  : { scale: 'point' as any, allowDuplicatedCategory: false, padding: { left: opts.xAxisPaddingLeft, right: opts.xAxisPaddingRight } as any })}
                tick={opts.showAxisLabels ? { fill: theme.palette.text.secondary, fontSize: 12 } : false}
                axisLine={opts.showAxisLine}
                tickLine={opts.showTickLines ? { stroke: theme.palette.text.secondary, strokeOpacity: opts.fadeTicksOnHover ? (isHovering ? opts.tickOpacityHover : 0) : opts.tickOpacityHover } : false}
                tickSize={opts.tickSize}
                height={opts.xAxisHeight as any}
              />
              <YAxis
                domain={[0, 100]}
                tick={opts.showAxisLabels ? { fill: theme.palette.text.secondary, fontSize: 12 } : false}
                axisLine={opts.showAxisLine}
                tickLine={opts.showTickLines ? { stroke: theme.palette.text.secondary, strokeOpacity: opts.fadeTicksOnHover ? (isHovering ? opts.tickOpacityHover : 0) : opts.tickOpacityHover } : false}
                tickSize={opts.tickSize}
                width={opts.yAxisWidth as any}
              />
              {!isCollapsed && <Tooltip content={<CustomTooltip />} />}
              {opts.curveLines ? (
                <Line type={opts.lineType} dataKey="score" stroke={opts.useGradientStroke ? `url(#${gradientId})` : theme.palette.primary.main} dot={false} strokeWidth={opts.strokeWidth} isAnimationActive={opts.enableAnimation} strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                segments.map((seg, idx) => (
                  <Line key={idx} type={opts.lineType} dataKey="score" data={seg.data} stroke={seg.color} dot={false} strokeWidth={opts.strokeWidth} isAnimationActive={opts.enableAnimation} strokeLinecap="round" strokeLinejoin="round" />
                ))
              )}
              <Line type={opts.lineType} dataKey="score" stroke="transparent" dot={renderDot} isAnimationActive={opts.enableAnimation} strokeLinecap="round" strokeLinejoin="round" />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Box sx={{ height: effectiveHeight, transition: `height ${opts.expandCollapseDurationMs}ms ${opts.expandCollapseEasing}` }}>
          <LineChart width={chartWidth} height={effectiveHeight} data={data} margin={opts.margin}
            onMouseMove={isCollapsed ? undefined : ((state: any) => {
              const idx = state && typeof state.activeTooltipIndex === 'number' ? state.activeTooltipIndex : null;
              if (idx !== null && idx !== hoveredIndex) {
                setHoveredIndex(idx);
                onPointHover && onPointHover(data[idx], idx);
              }
            })}
            onMouseLeave={isCollapsed ? undefined : (() => {
              setHoveredIndex(null);
              onPointHover && onPointHover(null, null);
            })}
            onClick={isCollapsed ? undefined : ((state: any) => {
              const idx = state && typeof state.activeTooltipIndex === 'number' ? state.activeTooltipIndex : null;
              if (idx !== null) {
                onPointClick && onPointClick(data[idx], idx);
              }
            })}
          >
            {opts.showGrid && (
              <CartesianGrid strokeDasharray={opts.gridStrokeDasharray} stroke={theme.palette.divider} strokeOpacity={opts.gridStrokeOpacity} />
            )}
            {opts.useGradientStroke && (
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                  {gradientStops.map((s, idx) => (
                    <stop key={idx} offset={`${s.offset}%`} stopColor={s.color} />
                  ))}
                </linearGradient>
              </defs>
            )}
            <XAxis
              type={xAxisType as any}
              dataKey={xDataKey}
              {...(!xIsEven
                ? { domain: ['dataMin', 'dataMax'] as any, tickFormatter: (ts: any) => dateTimeFormatter(ts, 'date_only'), padding: { left: opts.xAxisPaddingLeft, right: opts.xAxisPaddingRight } as any }
                : { scale: 'point' as any, allowDuplicatedCategory: false, padding: { left: opts.xAxisPaddingLeft, right: opts.xAxisPaddingRight } as any })}
              tick={opts.showAxisLabels ? { fill: theme.palette.text.secondary, fontSize: 12 } : false}
              axisLine={opts.showAxisLine}
              tickLine={opts.showTickLines ? { stroke: theme.palette.text.secondary, strokeOpacity: opts.fadeTicksOnHover ? (isHovering ? opts.tickOpacityHover : 0) : opts.tickOpacityHover } : false}
              tickSize={opts.tickSize}
              height={opts.xAxisHeight as any}
            />
            <YAxis
              domain={[0, 100]}
              tick={opts.showAxisLabels ? { fill: theme.palette.text.secondary, fontSize: 12 } : false}
              axisLine={opts.showAxisLine}
              tickLine={opts.showTickLines ? { stroke: theme.palette.text.secondary, strokeOpacity: opts.fadeTicksOnHover ? (isHovering ? opts.tickOpacityHover : 0) : opts.tickOpacityHover } : false}
              tickSize={opts.tickSize}
              width={opts.yAxisWidth as any}
            />
            {!isCollapsed && <Tooltip content={<CustomTooltip />} />}
            {opts.curveLines ? (
              <Line type={opts.lineType} dataKey="score" stroke={opts.useGradientStroke ? `url(#${gradientId})` : theme.palette.primary.main} dot={false} strokeWidth={opts.strokeWidth} isAnimationActive={opts.enableAnimation} strokeLinecap="round" strokeLinejoin="round" />
            ) : (
              segments.map((seg, idx) => (
                <Line key={idx} type={opts.lineType} dataKey="score" data={seg.data} stroke={seg.color} dot={false} strokeWidth={opts.strokeWidth} isAnimationActive={opts.enableAnimation} strokeLinecap="round" strokeLinejoin="round" />
              ))
            )}
            <Line type={opts.lineType} dataKey="score" stroke="transparent" dot={renderDot} isAnimationActive={opts.enableAnimation} strokeLinecap="round" strokeLinejoin="round" />
          </LineChart>
        </Box>
      )}
      {latest?.generating && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Generating latest review…
        </Typography>
      )}
      {latest?.error && (
        <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
          Failed to generate latest review: {latest.error}
        </Typography>
      )}
    </Box>
  );
};

export default ConversationQualityTimeline;
